<!-- eslint-disable no-undef -->
<template>
	<div ref="complexChartRef" class="complex-chart-wrapper" :class="{ mobile: isMobile }">
		<div
		v-if="graphData[0][0] !== 'empty' && isMobile"
		class="center-percentage">
			{{ `${centerPercentageParams.parsedPercentage === 0 && centerPercentageParams.percentage !== 0 ? '≈ ' : ''}${centerPercentageParams.parsedPercentage}` }}%
		</div>
		<div :id="`myPieChart${identifier}`" :style="{'pointer-events': props.isMobile ? 'none' : 'all'}"></div>
		<div class="shadow-try"></div>
		<div class="tooltip-wrapper" v-show="showTooltip || showZeroTooltip" :class="{'cluster-tooltip': clusterTooltip}">
			<div
			class="tooltip-center-point">
				<div
				ref="toolTipRef"
				class="tooltip-element"
				:style="parsingStyles"
				@mouseenter="mouseEnterHandlerCluster()"
				@mouseleave="mouseOutHandlerCluster()">
					<slot></slot>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
/* Vue */
import { ref, computed, watch, onMounted } from 'vue'

require('@/assets/localPlugins/d3pie')

/* Emmitters */
const emit = defineEmits(['mouseHover', 'mouseOut', 'sliceClicked', 'chartIsRendered'])

/** props received
@graphData {Array}
@colors {Array} default values ['#9AA0A8', '#A7C4B5', '#A9D8B8', '#BEFFC7', '#72705B', '#8AD8BA']
@hideLabels {Boolean} default is false
@hoverIndex {Number, String}
@showZeroTooltip {Boolean}
@clusterTooltip {Boolean}
@centerPercentageParams {Object}
@isMobile {Boolean}
@customSize {Number}
*/
const props = defineProps({
	identifier: String,
	graphData: Array,
	colors: {
		type: Array,
		default: () => { return ['#9AA0A8', '#A7C4B5', '#A9D8B8', '#BEFFC7', '#72705B', '#8AD8BA'] }
	},
	hideLabels: {
		type: Boolean,
		default: () => { return false }
	},
	hoverIndex: [Number, String],
	showZeroTooltip: Boolean,
	clusterTooltip: Boolean,
	centerPercentageParams: Object,
	isMobile: Boolean,
	customSize: Number,
	canvasWidth: Number
})

const showTooltip = ref(false)
const tolltipOvered = ref(false)
const arcCoords = ref([])
const centerCoords = ref({})
const onlyOneSliceChart = ref(false)
const pie = ref(null)
const colorsArray = ref([])
const complexChartRef = ref('')
const toolTipRef = ref()

const parsingStyles = computed(() => {
	if (props.showZeroTooltip || onlyOneSliceChart.value) {
		return { top: '30px', left: '240px', transform: 'translateX(20px)' }
	} else if (props.clusterTooltip) {
		return { top: '30px', left: '240px', transform: 'translateX(-30px)' }
	} else if (arcCoords.value?.length) {
		let auxTop = arcCoords.value[0].y + (arcCoords.value[0].y - arcCoords.value[1].y)
		let auxLeft = arcCoords.value[0].x + (arcCoords.value[0].x - arcCoords.value[1].x)

		auxTop > centerCoords.value.y ? auxTop += 40 : auxTop -= 30
		auxLeft > centerCoords.value.x ? auxLeft += 70 : auxLeft -= 75

		return { top: `${auxTop}px`, left: `${auxLeft}px` }
	} else {
		return {}
	}
})

watch(() => props.hoverIndex, (newVal, oldVal) => {
	newVal >= 0 ? mouseoverhandler(newVal) : mouseouthandler(oldVal)
})

watch(() => props.showZeroTooltip, (newVal, oldVal) => {
	if (document.getElementsByClassName('mygraph' + props.identifier + 'pieChart')[0]) {
		document.getElementsByClassName('mygraph' + props.identifier + 'pieChart')[0].style.opacity = props.showZeroTooltip ? 0.1 : 1
	}
})

const initChart = () => {
	let aproxCharsForLabel

	if (props.canvasWidth) {
		const aproxLettersFor100px = 24
		aproxCharsForLabel = ((props.canvasWidth / 4) * aproxLettersFor100px) / 100
	}

	pie.value = new d3pie(document.getElementById(`myPieChart${props.identifier}`), { //eslint-disable-line
		header: {},
		footer: {},
		size: {
			canvasHeight: props.customSize ? props.customSize : !props.isMobile ? 196 : 235,
			canvasWidth: props.canvasWidth ? props.canvasWidth : 332,
			pieInnerRadius: '62%',
			pieOuterRadius: !props.isMobile ? '100%' : '100%'
		},
		data: buildChartData(),
		labels: {
			outer: {
				hideWhenLessThanPercentage: props.hideLabels ? 101 : null,
				pieDistance: 18
			},
			inner: { format: 'none' },
			mainLabel: {
				fontSize: 12,
				fontWeight: 400,
				color: 'var(--secondary-dark-4)',
				font: 'Rubik'
			},
			value: {
				fontSize: 12,
				fontWeight: 700,
				color: '#859db0',
				font: 'Rubik'
			},
			lines: { enabled: !props.hideLabels, style: 'straight' },
			truncation: { enabled: true, truncateLength: props.isMobile ? 25 : (aproxCharsForLabel || 50) }
		},
		effects: {
			expansionOnMouseover: {
				effect: true
			},
			opacityOthersOnMouseover: {
				effect: true
			}
		},
		misc: {
			cssPrefix: `mygraph${props.identifier}`,
			colors: {
				segmentStroke: 'transparent'
			}
		},
		callbacks: {
			onload: () => { onChartLoadSuccess() },
			onMouseoverSegment: mouseHoverHandler,
			onMouseoutSegment: mouseOutHandler,
			onClickSegment: segmentClickedHandler
		}
	})
}

const mouseHoverHandler = params => {
	arcCoords.value = params.pie.lineCoordGroups[params.index]
	centerCoords.value = params.pie.pieCenter
	onlyOneSliceChart.value = params.data?.value === 100
	showTooltip.value = true
	emit('mouseHover', params)
}

const mouseOutHandler = (params, a, b) => {
	if (!props.clusterTooltip) {
		showTooltip.value = false
		emit('mouseOut')
	} else {
		setTimeout(() => {
			if (!tolltipOvered.value) {
				showTooltip.value = false
				emit('mouseOut')
			}
		}, 0)
	}
}

const mouseEnterHandlerCluster = () => {
	tolltipOvered.value = true
}

const mouseOutHandlerCluster = () => {
	tolltipOvered.value = false
	showTooltip.value = false
	emit('mouseOut')
}

const segmentClickedHandler = params => {
	emit('sliceClicked', params)
}

const onChartLoadSuccess = () => {
	emit('chartIsRendered', true)
	/* we need to change the overflow to visible, to make
	* sure all the legends appear correctly */
	const element = document.getElementById('myPieChart')
	if (element) {
		element.querySelectorAll('svg')[0].style.overflow = 'visible'
		/* due to limitations on how the labels also got shadow for now it is disabled (design choice, not my own) */
		/* element.querySelectorAll('svg')[0].style.filter = 'drop-shadow( -3px 7px 5px rgba(0,0,0,.15) )' */
		if (element.querySelectorAll('svg > text').style) element.querySelectorAll('svg > text').style.fontWeight = 'bold'
	}
}

const buildChartData = () => {
	const colors = getValidColorArray()
	var content = props.graphData.map((ele, index) => {
		return {
			label: ele[0],
			value: ele[1],
			color: colors[index],
			sliceId: ele[2] // added this to content, so we can use sliceId to know which slice we over
		}
	})

	return { content: content }
}

const replicateArray = (array, n) => {
	// Create an array of size "n" with undefined values
	var arrays = Array.apply(null, new Array(n))
	// Replace each "undefined" with our array, resulting in an array of n copies of our array
	arrays = arrays.map(arrayEntry => { return array })
	// Flatten our array of arrays
	return [].concat.apply([], arrays)
}

const getValidColorArray = () => {
	/* in order to draw the leader lines in the color we need
	* the colors array must be big enough to match all the data
	* so when there is not enough colors, we need to replicate the array
	* until we have enough colors */
	colorsArray.value = []
	if (props.colors.length < props.graphData.length) {
		colorsArray.value = replicateArray(props.colors, Math.round(props.graphData.length / props.colors.length))
	} else {
		colorsArray.value = props.colors
	}
	return colorsArray.value
}

const mouseoverhandler = index => {
	// eslint-disable-next-line no-undef
	const arcs = [...(d3.selectAll('.mygraph' + props.identifier + 'arc')._groups[0])]
	const event = new CustomEvent('mouseover')
	if (arcs[index]) arcs[index].dispatchEvent(event)
}

const mouseouthandler = index => {
	// eslint-disable-next-line no-undef
	const arcs = [...(d3.selectAll('.mygraph' + props.identifier + 'arc')._groups[0])]
	const event = new CustomEvent('mouseout')
	if (arcs[index]) arcs[index].dispatchEvent(event)
}

onMounted(() => {
	emit('chartIsRendered', false)
	initChart()
})
</script>

<style lang="scss" scoped>
.complex-chart-wrapper {
	position: relative;
	width: 600px;

	.center-percentage {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);

		font-size: 28px;
		font-weight: 500;
		color: var(--secondary-dark-2);
	}

	.tooltip-wrapper {
		position: absolute;
		width:100%;
		height:100%;
		top: 0;

		display: flex;
		align-items: center;
		justify-content: center;

		pointer-events: none;

		z-index: 1;

		&.cluster-tooltip {
			.tooltip-element {
				pointer-events: all;
			}
		}

		.tooltip-center-point {
			position: relative;
			width: 100%;
			height: 100%;

			.tooltip-element {
				position: absolute;
				width: auto;
				height: auto;

				transform: translate(-50%, -50%);
				z-index: 1;
			}
		}
	}

	&.mobile {
		width: auto;
	}
}
</style>
